import { FieldQueryOptions } from '@cibo/core'
import { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { uniq } from 'ramda'
import { PropsWithChildren, ReactNode, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBatchedFields } from '../../queries'
import { useLandManagement } from '../LandManagementProvider'
import { HideFilters, PaginatedFieldsContext } from './PaginatedFieldsContext'

export const PAGE_SIZES = [10, 25, 50]

type PaginatedFieldsProviderProps = {
  hideFilters?: HideFilters
  children?: ReactNode
}

export const PaginatedFieldsProvider = ({
  children,
  hideFilters = {},
}: PropsWithChildren<PaginatedFieldsProviderProps>) => {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<FieldQueryOptions>({})
  const [baseFilters, setBaseFilters] = useState<FieldQueryOptions>({})

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  })
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  const clearSelectionModel = () => selectionModel?.ids.clear()

  const selectedRowIds = useMemo(
    () => Array.from(selectionModel?.ids.values()),
    [selectionModel.ids]
  )
  const fields = useBatchedFields(selectedRowIds as string[])

  const { setDeleteModal, setLabelModal, setAssignFieldsModal, LAND_MANAGER_ROUTES } =
    useLandManagement()

  const handleDeselectRowIds = (ids: GridRowId[]) => {
    ids.forEach(id => selectionModel.ids.delete(id))
  }

  const selectedFields = fields.data?.filter(Boolean) || []

  const ownedByUsers = uniq(
    selectedFields ? selectedFields.filter(Boolean).map(f => f.ownedBy?.userId) : []
  )

  return (
    <PaginatedFieldsContext.Provider
      value={{
        filters,
        setFilters,
        hideFilters,
        baseFilters,
        setBaseFilters,

        isReady: !fields.isPending,

        selectedFields,
        selectionModel,
        setSelectionModel,
        selectedRowIds,
        ownedByUsers,
        deselectRowIds: handleDeselectRowIds,
        clearSelectionModel,

        page,
        setPage,

        pageSize,
        setPageSize,

        onDelete: (fieldId?: string) =>
          setDeleteModal({
            open: true,
            selectedIds: fieldId ? [fieldId] : (selectedRowIds as string[]),
            onSuccess: clearSelectionModel,
          }),

        onLabel: () =>
          setLabelModal({
            open: true,
            selectedIds: selectedRowIds as string[],
          }),
        onAssignFields: () =>
          setAssignFieldsModal({
            open: true,
            selectedIds: selectedRowIds as string[],
          }),
        onAddInformation: () => {
          navigate(LAND_MANAGER_ROUTES.PROFILE_FIELDS, {
            state: {
              profileFieldIds: selectedRowIds as string[],
            },
          })
        },
      }}
    >
      {children}
    </PaginatedFieldsContext.Provider>
  )
}
